import React from 'react';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';

const WhyWhatsAppBreaksArticle = () => (
    <Box>
        <Typography variant="h3" component="h1" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.65rem', md: '2rem' } }}>
            Why WhatsApp Breaks as a Field Operations System
        </Typography>
        <Typography color="text.secondary" fontWeight={600} paragraph sx={{ fontStyle: 'italic' }}>
            A message is not a record.
        </Typography>
        <Typography color="text.secondary" paragraph>
            <strong>TL;DR:</strong> WhatsApp is a coordination tool. It was never built to produce a retrievable, structured
            record of what was delivered. Home rehab centres that treat it as both a coordination channel and a documentation
            system hit the same wall: payout disputes that can&apos;t be settled from fact, insurance claims rejected for
            incomplete records, and session data buried in threads no one can reconstruct. The fix is not a more detailed
            message. It is a verified record created at point of delivery, independent of any chat thread.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Most rehab centre owners did not sit down one day and decide WhatsApp would run their field operations. It started
            small. Assignments went out in the morning. Therapists confirmed they were on the way. A schedule change took
            seconds. Nobody was complaining.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Then, without anyone noticing, the same thread became the record. End-of-day summaries started coming in from
            memory, whenever therapists got around to it. Travel got reported by message. Payout queries got sorted over chat.
            Documentation got chased across threads. It accumulated.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Priya manages 14 field therapists across Bengaluru. When a patient&apos;s family disputed a visit six weeks after
            it happened, she spent two hours going through group threads and individual chats before accepting she could not
            piece it together. The session had almost certainly happened. She had nothing usable to show for it.
        </Typography>
        <Typography color="text.secondary" paragraph>
            A message confirms a conversation took place. It does not confirm a session was delivered, what was documented, or
            what the therapist is owed. That is where the cost comes from, and it shows up in three specific places.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            Where does the breakdown actually show up?
        </Typography>
        <Typography color="text.secondary" paragraph>
            Most centres hit one of three failure points. Some hit all three before they change anything.
        </Typography>

        <Typography variant="h3" component="h3" fontWeight={700} sx={{ fontSize: '1.15rem', mt: 3, mb: 1 }}>
            Payout disputes
        </Typography>
        <Typography color="text.secondary" paragraph>
            Rajan runs a rehab centre in Pune with 11 therapists. At the end of every month, he reconciles payouts by going
            back through WhatsApp threads and tallying up what each therapist reported. His therapists do the same exercise
            independently. The numbers almost never match. With no verified record to go back to, the dispute lands with whoever
            argues harder or whoever Rajan decides to trust that month.
        </Typography>
        <Typography color="text.secondary" paragraph>
            This is the dominant financial pain for centres managing field teams, and it rarely registers as a single large
            problem. It is small, repeated discrepancies compounding quietly every month. The admin thread chasing alone can
            eat half a day per payment cycle, and that cost never shows up in any budget.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Related:{' '}
            <Link to="/blog/payout-disputes-home-rehab-fix">
                Payout Disputes in Home Rehab: Can Your System Fix Them?
            </Link>
            .
        </Typography>

        <Typography variant="h3" component="h3" fontWeight={700} sx={{ fontSize: '1.15rem', mt: 3, mb: 1 }}>
            Insurance claim rejections
        </Typography>
        <Typography color="text.secondary" paragraph>
            A session gets delivered. The therapist sends the usual confirmation message. Three weeks later, the claim gets
            submitted. The insurer flags it for incomplete documentation. What they need is a structured clinical record
            completed at point of delivery: what was assessed, what was treated, what the plan is for the next visit. What the
            centre has is a WhatsApp message and a note the therapist wrote from memory a few days later.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The claim gets rejected, the revenue for that session is gone, and the ops head spends hours trying to reconstruct
            records that should have been created the moment the session ended. Notes written retrospectively rarely meet the
            insurer&apos;s standard. Once a centre starts submitting claims at any real volume, home rehab documentation drift
            becomes a billing problem, not just an admin one.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Related:{' '}
            <Link to="/blog/why-insurance-claims-rejected-home-rehab">
                Why Insurance Claims Get Rejected in Home Rehab
            </Link>
            .
        </Typography>

        <Typography variant="h3" component="h3" fontWeight={700} sx={{ fontSize: '1.15rem', mt: 3, mb: 1 }}>
            Audit and compliance exposure
        </Typography>
        <Typography color="text.secondary" paragraph>
            If someone asked you right now to produce a complete, structured record of every session your therapists delivered
            in the last three months, how long would it take?
        </Typography>
        <Typography color="text.secondary" paragraph>
            For most centres on WhatsApp, the honest answer is: days of effort, a record full of gaps, and some sessions that
            cannot be reconstructed at all. That exposure exists now, for every session delivered without a verified record
            behind it.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Beyond audits, running operations on personal WhatsApp accounts means the centre has no governance over what sits
            on those devices. Patient names, session details, clinical notes, all sitting on phones the centre has no control
            over. Under DPDP, patient data governance is a live legal requirement. When a therapist leaves, they take that data
            with them, and the centre has no way to recover it.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            Why adding steps to WhatsApp does not fix the problem
        </Typography>
        <Typography color="text.secondary" paragraph>
            The instinct when the gap becomes visible is to add a layer: ask therapists to send more detailed messages, create a
            summary format for the group, have admin update a spreadsheet from the thread.
        </Typography>
        <Typography color="text.secondary" paragraph>
            It makes the problem less visible without actually making it go away.
        </Typography>
        <Typography color="text.secondary" paragraph>
            A more detailed WhatsApp message is still written from memory, sent whenever the therapist gets to it, with no
            required fields and nothing to verify it against. Moving that information into a spreadsheet is just transcribing one
            informal record into another. Manual transcription brings its own errors, and the admin burden does not shrink; it
            shifts.
        </Typography>
        <Typography color="text.secondary" paragraph>
            The only way to close the manual documentation gap is to replace the informal record entirely with a verified
            record created at point of delivery, before the therapist leaves the patient&apos;s home.
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            What changes when you have a verified session record
        </Typography>
        <Typography color="text.secondary" paragraph>
            Centres that move off WhatsApp as a documentation system do not stop using it for coordination. What changes is
            where the record lives and when it gets created.
        </Typography>
        <Typography color="text.secondary" paragraph>
            When a session is completed, the record is created on-site. Required fields get filled before the therapist leaves.
            It is structured, timestamped, and retrievable by session, therapist, or date. No searching through chat history. No
            asking anyone what they remember.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Payout calculations draw from that record, and insurance submissions reference it directly. If a dispute or audit
            comes up, the record is there and the numbers are not up for negotiation.
        </Typography>

        <TableContainer component={Paper} variant="outlined" sx={{ my: 3 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700 }} />
                        <TableCell sx={{ fontWeight: 700 }}>WhatsApp</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Verified session record</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {[
                        ['Created when', 'After the session, from memory', 'At point of delivery, on-site'],
                        ['Required fields', 'None', 'Yes, enforced'],
                        ['Retrievable months later', 'Buried in threads', 'Immediately, by session'],
                        ['Usable for insurance', 'No', 'Yes'],
                        ['Settles payout disputes', 'No', 'Yes'],
                        ['DPDP-ready storage', 'No', 'Yes'],
                    ].map(([label, wa, ver]) => (
                        <TableRow key={label}>
                            <TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
                            <TableCell>{wa}</TableCell>
                            <TableCell>{ver}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>

        <Typography color="text.secondary" paragraph>
            Related:{' '}
            <Link to="/blog/how-to-evaluate-software-field-therapy-teams">
                How to Evaluate Software for Field-Based Therapy Teams
            </Link>
            .
        </Typography>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 4, mb: 1.5 }}>
            Why meDZhub
        </Typography>
        <Typography color="text.secondary" paragraph>
            meDZhub was built specifically for home rehab centres managing field teams. Not adapted from a clinic management
            tool, not a generic EMR with a home visit module added after the fact. The system was designed around the actual
            workflow: therapists moving between patient homes every day, not working inside a facility.
        </Typography>
        <Typography color="text.secondary" paragraph>
            It handles field coordination while creating a verified session record at point of delivery. That record is
            structured, linked to the right patient and therapist, and connected directly to payout calculation. When the
            payment cycle runs, it draws from what was confirmed to have happened. Payout disputes do not get resolved by
            negotiation when the record exists and is not ambiguous.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Documentation follows a structured clinical format with required fields, which means records are audit-ready and
            usable for insurance submissions from the moment the session ends. Patient data is governed at the platform level,
            built with DPDP readiness by design, not retrofitted after the fact.
        </Typography>
        <Typography color="text.secondary" paragraph>
            Centres on meDZhub currently track over 1,000 sessions monthly across 35 active therapists. The WhatsApp group
            stays. It just stops being the place where operations are stored.
        </Typography>
        <Typography color="text.secondary" paragraph sx={{ mt: 2 }}>
            See what a verified session record looks like in practice.
        </Typography>
        <Button component={Link} to="/demo" variant="contained" sx={{ mt: 1, mb: 4 }} data-gtm="Blog-whatsapp-demo">
            Book a demo
        </Button>

        <Typography variant="h2" component="h2" fontWeight={700} sx={{ fontSize: '1.35rem', mt: 2, mb: 2 }}>
            Frequently asked questions
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Can WhatsApp be used as the record of a home therapy session for insurance purposes?
        </Typography>
        <Typography color="text.secondary" paragraph>
            No. Insurers require a structured clinical record completed at point of delivery. A WhatsApp confirmation message
            does not meet that standard, and notes filled in retrospectively typically do not either.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            What is the difference between session coordination and session documentation?
        </Typography>
        <Typography color="text.secondary" paragraph>
            Coordination is the communication that gets a therapist to the right patient at the right time. Documentation is
            the structured record of what happened during the session. WhatsApp handles coordination adequately. It cannot
            produce documentation that holds up for insurance claims, payout disputes, or audits.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            What happens when a payout dispute cannot be resolved from a verified record?
        </Typography>
        <Typography color="text.secondary" paragraph>
            It gets resolved by negotiation, by whoever argues harder, or by the centre absorbing the cost. A verified
            session record removes that ambiguity: the payout is calculated from what was confirmed to have happened, not
            from what was claimed.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            How do home rehab centres that have moved off WhatsApp handle field coordination?
        </Typography>
        <Typography color="text.secondary" paragraph>
            Coordination still happens through messaging. A separate system handles the session record, created on-site at
            point of delivery, structured, and retrievable independently of any chat thread. The two functions are separated
            rather than collapsed into one tool.
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
            At what team size does WhatsApp typically break down as an operations system?
        </Typography>
        <Typography color="text.secondary" paragraph>
            Most centres find it unmanageable past 8 to 10 therapists. Below that threshold, the session volume can still be
            tracked manually, even if it takes longer than it should. Once a team crosses that point, the data burial, dispute
            frequency, and admin overhead make the cost of staying on informal systems too hard to ignore.
        </Typography>
    </Box>
);

export default WhyWhatsAppBreaksArticle;
